import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { Card, CardBody, Button } from "reactstrap";
import AirHomeLoader from "../../../components/Common/AirHomeLoader";
import Request from "../../../helpers/request";
import * as url from "../../../helpers/url_helper";
import { toast } from "react-toastify";
import { convertProfileLocalDate } from "../../../Utils/configController";
import DeleteFlow from "./DeleteFlow";

interface FlowTableProps {
  onAddFlow?: () => void;
  onEditFlow?: (row: any) => void;
}

function FlowTable({ onAddFlow, onEditFlow }: FlowTableProps) {
  const [tableData, setTableData] = useState<any[]>([]);
  const [tablePage, setTablePage] = useState(1);
  const [tablePerPage, setTablePerPage] = useState(10);
  const [tableSearchText, setTableSearchText] = useState("");
  const [tableLoading, setTableLoading] = useState(false);
  const [tableTotalRows, setTableTotalRows] = useState(0);
  const tableFetchingRef = useRef(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteFlowData, setDeleteFlowData] = useState<any>(null);

  const handleDelete = (row: any) => {
    setDeleteFlowData(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const formatSchedule = (schedule: any) => {
    if (!schedule) return "-";
    if (schedule.type === "interval" && schedule.everyMinutes != null) {
      return `Every ${schedule.everyMinutes} min`;
    }
    if (schedule.type === "cron" && schedule.minute != null) {
      return `Cron (min: ${schedule.minute})`;
    }
    return schedule.type || "-";
  };

  const columns = [
    {
      name: "Name",
      sortable: false,
      cell: (row: any) => (
        <div style={{ cursor: "pointer", minWidth: "200px" }}>
          <div style={{ fontWeight: "bold" }}>{row.name}</div>
          <div style={{ fontSize: "12px", color: "#6c757d" }}>
            {row.description || "-"}
          </div>
        </div>
      ),
    },
    {
      name: "Segment",
      sortable: false,
      cell: (row: any) => (
        <div style={{ cursor: "pointer", minWidth: "140px" }}>
          {row.segment?.name || "-"}
        </div>
      ),
    },
    {
      name: "Schedule",
      sortable: false,
      cell: (row: any) => (
        <div style={{ cursor: "pointer", minWidth: "120px" }}>
          {formatSchedule(row.schedule)}
        </div>
      ),
    },
    {
      name: "Notification",
      sortable: false,
      cell: (row: any) => (
        <div style={{ cursor: "pointer", minWidth: "160px" }}>
          <div style={{ fontWeight: "500" }}>
            {row.notification?.title || "-"}
          </div>
          {row.notification?.body && (
            <div
              style={{
                fontSize: "12px",
                color: "#6c757d",
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.notification.body}
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Last Updated",
      sortable: false,
      cell: (row: any) => (
        <div style={{ cursor: "pointer", minWidth: "120px" }}>
          <div style={{ fontSize: "12px" }}>
            {convertProfileLocalDate(row.updatedAt) || "-"}
          </div>
        </div>
      ),
    },
    {
      name: "Status",
      sortable: false,
      cell: (row: any) => {
        const statusBadge = row?.isActive ? (
          <span className="font-size-11 badge-soft-success badge bg-secondary">
            Active
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-secondary">
            Inactive
          </span>
        );
        return <div style={{ cursor: "pointer" }}>{statusBadge}</div>;
      },
    },
    {
      name: "Actions",
      sortable: false,
      cell: (row: any) => (
        <div className="d-flex gap-2" style={{ cursor: "pointer" }}>
          <div
            onClick={() => onEditFlow?.(row)}
            title="Edit Flow"
            style={{ cursor: "pointer" }}
          >
            <box-icon name="pencil" size="20px" color="#454545"></box-icon>
          </div>
          <div
            onClick={() => handleDelete(row)}
            title="Delete Flow"
            style={{ cursor: "pointer" }}
          >
            <box-icon name="trash" size="20px" color="#454545"></box-icon>
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setTableLoading(true);
      if (tableFetchingRef.current) return;
      tableFetchingRef.current = true;

      try {
        const reqObj = {
          draw: 1,
          start: (tablePage - 1) * tablePerPage,
          length: tablePerPage,
          search: {
            value: tableSearchText || "",
          },
        };

        const response = await Request.post(
          `${url.BASE_URL}${url.LIST_FLOW}`,
          reqObj
        );

        setTableData(response.data?.data || []);
        setTableTotalRows(response.data?.recordsTotal ?? 0);
      } catch (error: any) {
        console.error("Error fetching flow data:", error);
        if (error?.response && error.response.status === 401) {
          localStorage.removeItem("userInfo");
          localStorage.removeItem("selectedPremiseID");
          localStorage.removeItem("selectedUserID");
          localStorage.removeItem("selectedUserEmail");
          localStorage.removeItem("Authorization");
          localStorage.removeItem("permissions");
          window.location.reload();
        } else {
          toast.error("Failed to fetch flow data");
        }
      } finally {
        setTableLoading(false);
        tableFetchingRef.current = false;
      }
    };

    fetchData();
  }, [tablePage, tablePerPage, tableSearchText, refreshKey]);

  return (
    <Card
      style={{
        marginTop: 100,
        marginLeft: 30,
        marginRight: 30,
        marginBottom: 0,
      }}
    >
      <CardBody>
        <div className="">
          <DataTable
            columns={columns}
            data={tableData}
            pagination
            paginationServer
            paginationTotalRows={tableTotalRows}
            onChangePage={(newPage) => setTablePage(newPage)}
            onChangeRowsPerPage={(newPerPage, newPage) => {
              setTablePerPage(newPerPage);
              setTablePage(newPage);
            }}
            highlightOnHover
            customStyles={customStyles}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            subHeader
            subHeaderComponent={
              <div className="container-fluid p-0 pb-2">
                <div className="row gx-2 gy-2 align-items-center">
                  <div className="col-12 col-md-6 d-flex flex-wrap align-items-center gap-2">
                    <h5 className="m-0 fw-bold" style={{ fontSize: "18px" }}>
                      Flows
                    </h5>
                  </div>
                  <div className="col-12 col-md-6 d-flex flex-wrap justify-content-md-end align-items-center gap-2">
                    <input
                      type="text"
                      placeholder="Search..."
                      className="form-control form-control-sm"
                      style={{ maxWidth: "150px" }}
                      value={tableSearchText}
                      onChange={(e) => {
                        setTableSearchText(e.target.value);
                        setTablePage(1);
                      }}
                    />

                    <Button
                      color="btn btn-dark"
                      size="sm"
                      onClick={() => {
                        if (onAddFlow) onAddFlow();
                      }}
                    >
                      Add Flow
                    </Button>
                  </div>
                </div>
              </div>
            }
          />
        </div>
        {tableLoading && <AirHomeLoader />}
      </CardBody>

      <DeleteFlow
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(false)}
        flowData={deleteFlowData}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </Card>
  );
}

export default FlowTable;

export const customStyles: any = {
  tableWrapper: {
    style: {
      minWidth: "1100px",
    },
  },
  subHeader: {
    style: {
      padding: "0px",
      margin: "0px",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#EFF2F7",
      fontWeight: "bold",
      fontSize: "14px",
      borderBottom: "none",
    },
  },
  rows: {
    style: {
      fontSize: "14px",
      color: "#343a40",
      borderBottom: "none",
    },
  },
  cells: {
    style: {
      fontSize: "14px",
      color: "#343a40",
      borderBottom: "none",
    },
  },
};
